/* eslint-disable react/jsx-no-useless-fragment */
import {
  Loading,
  TableColumnHeader,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import i18n from 'i18next'
import React from 'react'

import { SortButton } from '../../../components/SortButton/SortButton'
import {
  getOptimizationTypeStr,
  OPTIMIZATION_ORDERS,
  OPTIMIZATION_RESOURCES,
  OptimizationType,
} from '../../../types/dataset'
import { Filtering, FilteringValue } from '../../../types/filtering'
import {
  OptimizationProcess,
  RUNNING,
} from '../../../types/optimizationProcesses'
import { SortingDirection } from '../../../types/sorting'
import { formatDateTime } from '../../../utils/date'
import { getOptimizationProcessStatus } from '../../../utils/optimizationProcesses'
import { AbortButton } from './AbortButton'
import { DeleteButton } from './DeleteButton'
import { DownloadResultsButton } from './DownloadResultsButton'
import { EditButton } from './EditButton'
import { FilterByStatus } from './FilterByStatus'
import { FilterByText } from './FilterByText'
import { WizardButton } from './WizardButton'

const EllipsisText = ({ text }: { text?: string }) => (
  <div
    title={text || ''}
    style={{
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {text || '-'}
  </div>
)

type Columns = OptimizationProcess & {
  actions?: string
}

type ColumnsKeys = keyof Columns

type UseColumnsProps = {
  sorting: { [key in keyof Partial<Columns>]: SortingDirection }
  handleSort: (key: ColumnsKeys, direction: SortingDirection) => void
  filtering: Filtering<Columns> & Record<string, FilteringValue>
  handleFilter: (key: string, value: FilteringValue) => void
}

export const useColumns = ({
  sorting,
  filtering,
  handleSort,
  handleFilter,
}: UseColumnsProps): TableColumnInstance<Columns, ColumnsKeys>[] => {
  const columns: TableColumnInstance<Columns, ColumnsKeys>[] = [
    {
      id: 'id',
      width: 5,
      header: (
        <TableColumnHeader
          title={i18n.t('Id')}
          sorting={
            <SortButton
              sort={sorting?.id}
              onSort={(direction) => handleSort('id', direction)}
            />
          }
        />
      ),
      th: true,
      template: ({ id }) => `#${id}`,
    },
    {
      id: 'name',
      width: 12,
      noWrap: true,
      header: (
        <TableColumnHeader
          title={i18n.t('Name')}
          sorting={
            <SortButton
              sort={sorting?.name}
              onSort={(direction) => handleSort('name', direction)}
            />
          }
          filtering={
            <FilterByText
              filter={filtering?.name}
              onFilter={(value) => handleFilter('name', value)}
              label={i18n.t('Filter by name')}
            />
          }
        />
      ),
      th: true,
      template: ({ name }) => <EllipsisText text={name} />,
    },
    {
      id: 'notes',
      width: 26,
      noWrap: true,
      header: (
        <TableColumnHeader
          title={i18n.t('Notes')}
          filtering={
            <FilterByText
              filter={filtering?.notes}
              onFilter={(value) => handleFilter('notes', value)}
              label={i18n.t('Filter by notes')}
            />
          }
        />
      ),
      th: true,
      template: ({ notes }) => <EllipsisText text={notes} />,
    },
    {
      id: 'official',
      width: 7,
      header: <TableColumnHeader title={i18n.t('Official')} />,
      th: true,
      template: ({ official, optimizationProcessType }) => {
        if (optimizationProcessType === OPTIMIZATION_ORDERS) {
          return ''
        }
        return official ? i18n.t('Yes').toString() : i18n.t('No').toString()
      },
    },
    {
      id: 'optimizationProcessType',
      width: 7,
      header: (
        <TableColumnHeader
          title={i18n.t('Type')}
          sorting={
            <SortButton
              sort={sorting?.optimizationProcessType}
              onSort={(direction) =>
                handleSort('optimizationProcessType', direction)
              }
            />
          }
        />
      ),
      th: true,
      template: ({ optimizationProcessType }) =>
        optimizationProcessType
          ? getOptimizationTypeStr(optimizationProcessType as OptimizationType)
          : '-',
    },
    {
      id: 'sync',
      width: 16,
      header: (
        <TableColumnHeader
          title={i18n.t('Sync')}
          sorting={
            <SortButton
              sort={sorting?.sync}
              onSort={(direction) => handleSort('sync', direction)}
            />
          }
        />
      ),
      th: true,
      template: ({ sync, optimizationProcessType }) => {
        if (optimizationProcessType === OPTIMIZATION_RESOURCES) {
          return '-'
        }
        return sync ? formatDateTime(sync) : '-'
      },
    },
    {
      id: 'referenceOptimizationProcess',
      width: 12,
      header: (
        <TableColumnHeader
          title={i18n.t('Source Opt. proc')}
          sorting={
            <SortButton
              sort={sorting?.referenceOptimizationProcess}
              onSort={(direction) =>
                handleSort('referenceOptimizationProcess', direction)
              }
            />
          }
        />
      ),
      th: true,
      template: ({ referenceOptimizationProcess }) =>
        referenceOptimizationProcess ? `#${referenceOptimizationProcess}` : '-',
    },
    {
      id: 'version',
      width: 8,
      header: (
        <TableColumnHeader
          title={i18n.t('Version')}
          sorting={
            <SortButton
              sort={sorting?.version}
              onSort={(direction) => handleSort('version', direction)}
            />
          }
        />
      ),
      th: true,
      template: ({ version, optimizationProcessType }) => {
        if (optimizationProcessType === OPTIMIZATION_RESOURCES) {
          return '-'
        }
        return version || '-'
      },
    },
    {
      id: 'status',
      width: 8,
      header: (
        <TableColumnHeader
          title={i18n.t('Status')}
          sorting={
            <SortButton
              sort={sorting?.status}
              onSort={(direction) => handleSort('status', direction)}
            />
          }
          filtering={
            <FilterByStatus
              filter={filtering?.status}
              onFilter={(value) => handleFilter('status', value)}
            />
          }
        />
      ),
      template: ({ status }) => (
        <>
          {getOptimizationProcessStatus(status)}{' '}
          {status === RUNNING && <Loading />}
        </>
      ),
    },
    {
      id: 'user',
      width: 12,
      noWrap: true,
      header: (
        <TableColumnHeader
          title={i18n.t('User')}
          sorting={
            <SortButton
              sort={sorting?.user}
              onSort={(direction) => handleSort('user', direction)}
            />
          }
        />
      ),
      th: true,
      template: ({ user }) => <EllipsisText text={user} />,
    },
    {
      id: 'createdAt',
      width: 12,
      header: (
        <TableColumnHeader
          title={i18n.t('Created on')}
          sorting={
            <SortButton
              sort={sorting?.createdAt}
              onSort={(direction) => handleSort('createdAt', direction)}
            />
          }
        />
      ),
      template: ({ createdAt }) => formatDateTime(createdAt),
    },
    {
      id: 'actions',
      header: i18n.t('Actions'),
      noWrap: true,
      // align: 'right',
      width: 14,
      template: ({
        id,
        status,
        version,
        optimizationProcessType,
        name,
        notes,
        official,
      }) => (
        <>
          <AbortButton optimizationProcessId={id} status={status} />
          <EditButton
            optimizationProcessId={id}
            currentName={name}
            currentNotes={notes}
            currentOfficial={official}
            showOfficial={optimizationProcessType !== OPTIMIZATION_ORDERS}
          />
          <DownloadResultsButton optimizationProcessId={id} status={status} />
          <WizardButton
            optimizationProcessId={id}
            status={status}
            version={version}
            optimizationProcessType={optimizationProcessType}
          />
          <DeleteButton optimizationProcessId={id} status={status} />
        </>
      ),
    },
  ]
  return columns
}
